import api from "./apiClient";

// Get base URL from runtime config and replace /api/v2 with /agentic/api/v2
const BASE_URL = window.__RUNTIME_CONFIG__.REACT_APP_API_ENDPOINT;
const AGENTIC_API_BASE_URL = BASE_URL.replace(/\/api\/v2$/, "/agentic/api/v2");

/**
 * Send a message to the Marco AMS Agent with streaming response.
 *
 * @param {Object} params - Chat parameters
 * @param {string} params.message - User's message
 * @param {string} [params.sessionId] - Optional session ID for conversation continuity
 * @param {function} params.onChunk - Callback for each content chunk
 * @param {function} params.onStart - Callback when streaming starts (receives session_id)
 * @param {function} params.onComplete - Callback when streaming completes
 * @param {function} params.onError - Callback when an error occurs
 * @returns {Promise<void>}
 */
export async function streamChat({ message, sessionId, onChunk, onStart, onComplete, onError }) {
	try {
		const response = await api.request(`${AGENTIC_API_BASE_URL}/marco/chat/stream`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				message,
				session_id: sessionId || null,
			}),
		});

		// Read the SSE stream
		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";
		let currentSessionId = sessionId;

		while (true) {
			const { done, value } = await reader.read();

			if (done) {
				break;
			}

			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split("\n");

			// Keep the last potentially incomplete line in the buffer
			buffer = lines.pop() || "";

			for (const line of lines) {
				if (line.startsWith("data: ")) {
					const data = line.slice(6);

					try {
						const event = JSON.parse(data);

						switch (event.type) {
							case "start":
								currentSessionId = event.session_id;
								onStart?.(event.session_id);
								break;
							case "chunk":
								onChunk?.(event.content);
								break;
							case "complete":
								onComplete?.(event.session_id);
								break;
							case "error":
								onError?.(new Error(event.error));
								break;
							default:
								console.warn("Unknown event type:", event.type);
						}
					} catch (parseError) {
						console.warn("Failed to parse SSE event:", data, parseError);
					}
				}
			}
		}

		// Process any remaining data in the buffer
		if (buffer.trim()) {
			const lines = buffer.split("\n");
			for (const line of lines) {
				if (line.startsWith("data: ")) {
					const data = line.slice(6);
					try {
						const event = JSON.parse(data);
						if (event.type === "complete") {
							onComplete?.(event.session_id);
						}
					} catch (parseError) {
						// Ignore parse errors on final buffer
					}
				}
			}
		}

	} catch (error) {
		onError?.(error);
	}
}

/**
 * Send a message to the Marco AMS Agent (non-streaming).
 *
 * @param {Object} params - Chat parameters
 * @param {string} params.message - User's message
 * @param {string} [params.sessionId] - Optional session ID for conversation continuity
 * @returns {Promise<Object>} Response with { response, session_id, success }
 */
export async function sendChat({ message, sessionId }) {
	const response = await api.request(`${AGENTIC_API_BASE_URL}/marco/chat`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			message,
			session_id: sessionId || null,
		}),
	});

	return response.json();
}
